import { Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useListContractLines } from '@/api/generated/contracts/contracts';
import { useListUnits } from '@/api/generated/units/units';
import { useListPlannedItems } from '@/api/generated/work-structure/work-structure';
import { FormModal, ModalFooter, TextField } from '@/components/common';
import { findUnitLabel } from '@/features/units/unitDisplay';

/** Odabrana stavka za nacrt aktivnosti — točno jedan od plannedItemId/contractLineId. */
export interface ActivityPickedItem {
  plannedItemId?: string;
  contractLineId?: string;
  itemCode: string;
  description: string;
  unit: string;
}

interface ActivityItemPickerModalProps {
  opened: boolean;
  onClose: () => void;
  workPackageId?: string;
  contractId: string;
  existingPlannedItemIds: string[];
  existingContractLineIds: string[];
  /** Odabrane stavke idu u nacrt (NOVO) s količinom 0 — spremaju se tek gumbom "Spremi stavke". */
  onAdd: (items: ActivityPickedItem[]) => void;
}

interface PickerRow {
  id: string;
  itemCode: string;
  description: string;
  unitLabel: string;
  unit: string;
}

export const ActivityItemPickerModal = ({
  opened,
  onClose,
  workPackageId,
  contractId,
  existingPlannedItemIds,
  existingContractLineIds,
  onAdd
}: ActivityItemPickerModalProps) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: plannedItemsData } = useListPlannedItems(workPackageId ?? '', {
    query: { enabled: opened && !!workPackageId }
  });
  const { data: contractLinesData } = useListContractLines(contractId, {
    query: { enabled: opened && !workPackageId }
  });
  const { data: unitsData } = useListUnits();

  const usedPlannedIds = new Set(existingPlannedItemIds);
  const usedContractLineIds = new Set(existingContractLineIds);

  const rows: PickerRow[] = workPackageId
    ? (plannedItemsData ?? [])
        .filter((item) => item.id && !usedPlannedIds.has(item.id))
        .map((item) => ({
          id: item.id as string,
          itemCode: item.itemCode ?? '—',
          description: item.description ?? '',
          unitLabel: item.unit ?? '—',
          unit: item.unit ?? ''
        }))
    : (contractLinesData ?? [])
        .filter((line) => line.id && !usedContractLineIds.has(line.id))
        .map((line) => ({
          id: line.id as string,
          itemCode: line.itemCode ?? '—',
          description: line.description ?? '',
          unitLabel: findUnitLabel(unitsData, line.unitId, i18n.language),
          // Spremljeni redak nosi kod jedinice (snapshot), ne lokalizirani naziv — isto i nova stavka.
          unit: unitsData?.find((unit) => unit.id === line.unitId)?.code ?? ''
        }));

  const query = search.trim().toLowerCase();
  const filteredRows = rows.filter((row) => {
    if (!query) return true;
    return (
      row.itemCode.toLowerCase().includes(query) || row.description.toLowerCase().includes(query)
    );
  });

  const selectableIds = filteredRows.map((row) => row.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setSelected(new Set());
  };

  const handleAdd = () => {
    onAdd(
      rows
        .filter((row) => selected.has(row.id))
        .map((row) => ({
          ...(workPackageId ? { plannedItemId: row.id } : { contractLineId: row.id }),
          itemCode: row.itemCode,
          description: row.description,
          unit: row.unit
        }))
    );
    reset();
    onClose();
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={700}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('activityItemPicker.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <TextField
          label={t('activityItemPicker.searchLabel')}
          placeholder={t('activityItemPicker.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <Stack gap={0} style={{ maxHeight: 320, overflowY: 'auto' }}>
          <Group
            gap={8}
            wrap="nowrap"
            py={4}
            style={{ borderBottom: '1px solid var(--color-border-soft)' }}
          >
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              disabled={selectableIds.length === 0}
              onChange={toggleAll}
              aria-label={t('activityItemPicker.selectAll')}
            />
            <Text fz={11} fw={600} w={90} style={{ color: 'var(--color-text-muted)' }}>
              {t('activityItemPicker.colCode')}
            </Text>
            <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
              {t('activityItemPicker.colDescription')}
            </Text>
            <Text fz={11} fw={600} w={80} style={{ color: 'var(--color-text-muted)' }}>
              {t('activityItemPicker.colUnit')}
            </Text>
          </Group>

          {filteredRows.length === 0 && (
            <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
              {t('activityItemPicker.emptyState')}
            </Text>
          )}

          {filteredRows.map((row, index) => (
            <Group
              key={row.id}
              gap={8}
              wrap="nowrap"
              py={10}
              role="button"
              tabIndex={0}
              onClick={() => toggle(row.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') toggle(row.id);
              }}
              style={{
                cursor: 'pointer',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Checkbox
                checked={selected.has(row.id)}
                onChange={() => toggle(row.id)}
                onClick={(event) => event.stopPropagation()}
              />
              <Text fz={13} fw={600} w={90} style={{ color: 'var(--color-text-primary)' }}>
                {row.itemCode}
              </Text>
              <Text fz={13} style={{ flex: 1, color: 'var(--color-text-secondary)' }} truncate>
                {row.description}
              </Text>
              <Text fz={13} w={80} style={{ color: 'var(--color-text-muted)' }}>
                {row.unitLabel}
              </Text>
            </Group>
          ))}
        </Stack>

        <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
          {t('itemsDraft.pickerNote')}
        </Text>

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button disabled={selected.size === 0} onClick={handleAdd}>
            {t('activityItemPicker.addSelected', { count: selected.size })}
          </Button>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
